/** @file Generic sampler for scalar (curved) and flag (stepped) channels, driven by timelineChannels. */
import type { Sample } from '../../contracts/sample';
import type { SkeletonData, TimelineData } from '../../skeletonData';
import { evalCurve } from '../../curve/evalCurve';
import { locateKey, type KeyCursor } from './locateKey';
import { sampleIndex } from './sampleIndex';
import { timelineChannels, type ChannelKind } from './timelineChannels';

const cur: KeyCursor = { k0: 0, k1: 0, u: 0 };

type ChannelTimeline = Extract<TimelineData, { k: ChannelKind }>;

/** Samples one scalar or flag timeline into Sample.values as an absolute local value and sets its mask bit. */
export const channelHandler = {
  sample(tl: ChannelTimeline, time: number, lastTime: number, out: Sample, data: SkeletonData): void {
    if (!locateKey(tl.times, time, cur)) return;
    const v0 = tl.values[cur.k0] ?? 0;
    const v = 'curves' in tl && cur.k1 !== cur.k0 ? evalCurve(tl.curves.kind, tl.curves.cp, cur.k0, v0, tl.values[cur.k1] ?? v0, cur.u) : v0;
    const d = timelineChannels[tl.k];
    let i: number;
    let setup = 0;
    if ('bone' in tl) {
      i = sampleIndex(data, 'bone', tl.bone, d.offset);
      setup = data.bones[tl.bone]?.setup[d.offset] ?? 0;
    } else if ('slot' in tl) i = sampleIndex(data, 'slot', tl.slot, d.offset);
    else i = sampleIndex(data, 'ik', tl.ik, d.offset);
    out.values[i] = d.rel === 'mul' ? setup * v : d.rel === 'add' ? setup + v : v;
    out.mask[i] = 1;
  },
};
